import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/spinner';
import { generateDeviceId } from '@/lib/totp';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Check email verification requirement
  // Allow access to verify-email-pending and verify-email pages without verification
  const isVerificationPage = location.pathname === '/verify-email-pending' || location.pathname === '/verify-email';
  
  // Check both Firebase Auth and Firestore profile for email verification
  // If either shows verified, allow access (this handles sync issues)
  const isEmailVerified = currentUser.emailVerified || userProfile?.emailVerified;
  
  if (!isVerificationPage && !isEmailVerified) {
    console.log('❌ Email not verified (Auth:', currentUser.emailVerified, ', Profile:', userProfile?.emailVerified, '), redirecting to /verify-email-pending');
    return <Navigate to="/verify-email-pending" replace />;
  }

  // Check 2FA requirement
  if (userProfile?.twoFactorEnabled) {
    const deviceId = generateDeviceId();
    const trustedDevices = userProfile.trustedDevices || [];
    const isTrusted = trustedDevices.some((d) => d.id === deviceId);

    console.log('ProtectedRoute 2FA Check:', {
      path: location.pathname,
      deviceId,
      trustedDeviceIds: trustedDevices.map(d => d.id),
      isTrusted,
    });

    if (!isTrusted) {
      // Device not trusted, redirect to 2FA verification
      console.log('❌ Device not trusted, redirecting to /verify-2fa');
      return <Navigate to="/verify-2fa" state={{ from: location }} replace />;
    }
    
    console.log('✅ Device is trusted, allowing access');
  }

  // Check admin requirement
  if (requireAdmin) {
    if (!userProfile?.isAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

